import { mintTokensToWhitelistedWallet } from "@customer-gallery/redux/actionCreators/mintTokensToWhitelistedWallet";
import { whitelistCollectionTokensForWallet } from "@customer-gallery/redux/actionCreators/whitelistCollectionTokensForWallet";
import { NovelGalleryThunkAction } from "@customer-gallery/redux/galleryTypedRedux";
import { asyncDispatch } from "@customer-gallery/redux/utils/asyncDispatch";
import { ICryptoAddress } from "@novel/shared/interfaces/CryptoAddress";
import { ICryptoWallet } from "@novel/shared/interfaces/CryptoWallet";
import { ITokenCollectionClaimWithTokenCollection } from "@novel/shared/interfaces/TokenCollectionClaim";
import { typedFrontendGalleryApi } from "@customer-gallery/utils/typedFrontendGalleryApi";

export const loadUnclaimedTokens =
    (): NovelGalleryThunkAction<
        "LOADING_UNCLAIMED_TOKENS" | "ERROR_LOADING_UNCLAIMED_TOKENS" | "LOADED_UNCLAIMED_TOKENS"
    > =>
    async (dispatch) =>
        asyncDispatch(
            typedFrontendGalleryApi.getReq("/api/customer/unclaimed-tokens"),
            () =>
                dispatch({
                    type: "LOADING_UNCLAIMED_TOKENS",
                    payload: undefined,
                }),
            (failure) =>
                dispatch({
                    type: "ERROR_LOADING_UNCLAIMED_TOKENS",
                    payload: {
                        errorMessage: failure.error.message,
                    },
                }),
            ({ tokenCollectionClaims }) =>
                dispatch({
                    type: "LOADED_UNCLAIMED_TOKENS",
                    payload: {
                        tokenCollectionClaims,
                    },
                }),
        );

export const claimTokenLineItem = (
    cryptoAddress: ICryptoAddress,
    wallet: ICryptoWallet,
    tokenClaim: ITokenCollectionClaimWithTokenCollection,
): NovelGalleryThunkAction<
    | "LOADING_CLAIMING_TOKEN_LINE_ITEM"
    | "ERROR_LOADING_CLAIMING_TOKEN_LINE_ITEM"
    | "LOADED_CLAIMING_TOKEN_LINE_ITEM"
> => {
    const { collectionMetadata } = tokenClaim.collectionToMintFrom;
    cryptoAddress.address = cryptoAddress.address.toLowerCase();
    if (wallet.cryptoAddresses)
        wallet.cryptoAddresses = wallet.cryptoAddresses.map((cryptoAddress) => ({
            ...cryptoAddress,
            address: cryptoAddress.address.toLowerCase(),
        }));

    return async (dispatch) => {
        dispatch({
            type: "LOADING_CLAIMING_TOKEN_LINE_ITEM",
            payload: {
                claimId: tokenClaim.id,
            },
        });

        try {
            if (collectionMetadata.decentralizedMintEnabled) {
                // whitelisting the collection tokens, must be done in advance of minting for decentralized mint
                const whitelistResult = await dispatch(
                    whitelistCollectionTokensForWallet(cryptoAddress, wallet, tokenClaim),
                );

                if (whitelistResult.type === "ERROR_LOADING_WHITELIST_TOKENS_FOR_WALLET") {
                    return dispatch({
                        type: "ERROR_LOADING_CLAIMING_TOKEN_LINE_ITEM",
                        payload: {
                            claimId: tokenClaim.id,
                            errorMessage: whitelistResult.payload.errorMessage,
                        },
                    });
                }
            }

            // minting the token to the now-whitelisted wallet
            const mintResult = await dispatch(
                mintTokensToWhitelistedWallet(cryptoAddress, wallet, tokenClaim),
            );

            if (mintResult.type === "ERROR_MINTING_TOKENS_TO_WALLET") {
                return dispatch({
                    type: "ERROR_LOADING_CLAIMING_TOKEN_LINE_ITEM",
                    payload: {
                        claimId: tokenClaim.id,
                        errorMessage: mintResult.payload.errorMessage,
                    },
                });
            } else if (mintResult.type === "LOADING_MINTING_TOKENS_TO_WALLET") {
                // this logic branch should never happen, just needed for typescript compliance
                return dispatch({
                    type: "ERROR_LOADING_CLAIMING_TOKEN_LINE_ITEM",
                    payload: {
                        claimId: tokenClaim.id,
                        errorMessage: "minting tokens timed out",
                    },
                });
            }

            return dispatch({
                type: "LOADED_CLAIMING_TOKEN_LINE_ITEM",
                payload: {
                    cryptoAddress: mintResult.payload.cryptoAddress,
                    tokenCollectionClaim: mintResult.payload.tokenCollectionClaim,
                },
            });
        } catch (error: any) {
            dispatch({
                type: "ERROR_LOADING_CLAIMING_TOKEN_LINE_ITEM",
                payload: {
                    claimId: tokenClaim.id,
                    errorMessage: error.message,
                },
            });

            throw error;
        }
    };
};
